import React, { useEffect, useMemo, useState } from "react";
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Collapse,
    Typography,
    Card,
    CardContent,
    Grid,
    TextField,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    TableSortLabel,
    TablePagination,
    Paper,
    Skeleton,
} from "@mui/material";
import { KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";
import toastr from "toastr";
import { makeGETRequestOnUserService } from "../../Api/api";
import moment from "moment";

interface Referral {
    uuid: string;
    name: string;
    email: string;
    mobile: string;
    lead_owner: string;
    kyc_status: boolean;
    created_at: string;
    expires_at: string;
    lead_status: string;
    smest_id: string;
}

interface LeadOwner {
    uuid: string;
    lead_owner: string;
    lead_phone: string;
    lead_email: string;
    created_at: string;
    updated_at: string;
}

export interface ReferralSource {
    uuid: string;
    name: string;
    smest_id: string;
    mobile_number: string;
}

interface Client {
    client_uuid: string;
    client_name: string;
    client_email: string;
    client_mobile_number: string;
    client_lead_owner: LeadOwner | null;
    client_smest_id: string;
    client_kyc_status: boolean;
    referrals: Referral[];
    active_referral_users_count: number;
    invested_referral_count: number;
    reward_earned: number;
    reward_claimed: number;
    source: ReferralSource | null;
    created_at: string;
}

type Order = "asc" | "desc";
type SortKey =
    | "client_name"
    | "client_smest_id"
    | "client_email"
    | "client_mobile_number"
    | "client_lead_owner"
    | "active_referral_users_count"
    | "invested_referral_count"
    | "reward_earned"
    | "reward_claimed"
    | "source_name"
    | "client_kyc_status"
    | "created_at";

const FILTER_COLUMNS = [
    { label: "Source", value: "source_name" },
    { label: "Name", value: "client_name" },
    { label: "KYC", value: "client_kyc_status" },
    { label: "Email", value: "email" },
    { label: "Mobile", value: "mobile" },
    { label: "Lead Owner", value: "client_lead_owner" },
];

const ReferralsUsers: React.FC = () => {
    const [clients, setClients] = useState<Client[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [expandedRows, setExpandedRows] = useState<{ [k: string]: boolean }>({});

    const [selectedColumn, setSelectedColumn] = useState("");
    const [filterValue, setFilterValue] = useState<string>("");

    const [order, setOrder] = useState<Order>("asc");
    const [orderBy, setOrderBy] = useState<SortKey | null>(null);

    const [page, setPage] = useState<number>(0);
    const [rowsPerPage, setRowsPerPage] = useState<number>(10);

    useEffect(() => {
        let isMounted = true;
        setIsLoading(true);
        makeGETRequestOnUserService("user/all_referrals/")
            .then((resp: any) => {
                if (!isMounted) return;
                if (resp.status === 200) {
                    setClients(Array.isArray(resp.data) ? resp.data : []);
                } else {
                    toastr.error("Failed to load referrals");
                }
            })
            .catch((err: any) => {
                console.error(err);
                toastr.error("Failed to fetch client referrals");
            })
            .finally(() => {
                if (isMounted) setIsLoading(false);
            });
        return () => {
            isMounted = false;
        };
    }, []);

    const getColumnValue = (c: Client, col: string) => {
        switch (col) {
            case "source_name":
                return c.source?.name && c.source.name.trim() !== "" ? c.source.name : "SMEST";
            case "client_name":
                return c.client_name ?? "";
            case "mobile":
                return c.client_mobile_number ?? "";
            case "email":
                return c.client_email ?? "";
            case "client_kyc_status":
                return String(c.client_kyc_status)

            case "client_lead_owner":
                return c.client_lead_owner?.lead_owner ?? " ";
            default:
                return "";
        }
    };

    const filtered = useMemo(() => {
        if (!filterValue) return clients;
        const q = filterValue.toLowerCase();
        return clients.filter((c) => getColumnValue(c, selectedColumn).toLowerCase().includes(q));
    }, [clients, selectedColumn, filterValue]);

    const stableSort = (arr: Client[]) => {
        if (!orderBy) return arr;
        const comparator = (a: Client, b: Client) => {
            let aVal: any;
            let bVal: any;

            switch (orderBy) {
                case "client_kyc_status":
                    aVal = a.client_kyc_status ? 1 : 0;
                    bVal = b.client_kyc_status ? 1 : 0;
                    break;
                case "source_name":
                    aVal = a.source?.name ?? "";
                    bVal = b.source?.name ?? "";
                    break;
                case "client_lead_owner":
                    aVal = a.client_lead_owner?.lead_owner ?? "";
                    bVal = b.client_lead_owner?.lead_owner ?? "";
                    break;
                default:
                    aVal = (a as any)[orderBy];
                    bVal = (b as any)[orderBy];
            }

            const isNum = typeof aVal === "number" || typeof bVal === "number";
            if (isNum) {
                const an = Number(aVal || 0);
                const bn = Number(bVal || 0);
                return order === "asc" ? an - bn : bn - an;
            }
            const aStr = (aVal ?? "").toString().toLowerCase();
            const bStr = (bVal ?? "").toString().toLowerCase();
            if (aStr < bStr) return order === "asc" ? -1 : 1;
            if (aStr > bStr) return order === "asc" ? 1 : -1;
            return 0;
        };
        return [...arr].sort(comparator);
    };

    const sorted = useMemo(() => stableSort(filtered), [filtered, order, orderBy]);

    const paginated = useMemo(() => {
        const start = page * rowsPerPage;
        return sorted.slice(start, start + rowsPerPage);
    }, [sorted, page, rowsPerPage]);

    const handleRequestSort = (property: SortKey) => {
        const isAsc = orderBy === property && order === "asc";
        setOrder(isAsc ? "desc" : "asc");
        setOrderBy(property);
    };

    const handleChangePage = (_e: unknown, newPage: number) => setPage(newPage);
    const handleChangeRowsPerPage = (e: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(e.target.value, 10));
        setPage(0);
    };

    const toggleExpand = (uuid: string) => setExpandedRows((prev) => ({ ...prev, [uuid]: !prev[uuid] }));

    const fromRecord = filtered.length === 0 ? 0 : page * rowsPerPage + 1;
    const toRecord = Math.min((page + 1) * rowsPerPage, filtered.length);

    return (
        <Paper>
            <Grid sx={{ margin: "20px 0", padding: "0 20px" }}>
                <Grid container alignItems="center" spacing={3}>
                    <Grid item xs={12} sm={6}>
                        <Typography variant="h4">Referrals List</Typography>
                    </Grid>
                </Grid>
            </Grid>

            {isLoading ? (
                <Box sx={{ p: 2, height: "100vh", width: "100%" }}>
                    {[1, 2, 3, 4, 5].map((n) => (
                        <Skeleton key={n} height={50} sx={{ mb: 1 }} animation="wave" />
                    ))}
                </Box>
            ) : (
                <Box sx={{ height: "100vh", width: "100%", px: [2, 10] }}>
                    <Box display="flex" alignItems="center" p={1} gap={2}>
                        <FormControl size="small" sx={{ minWidth: 180 }}>
                            <InputLabel>Filter Column</InputLabel>
                            <Select
                                value={selectedColumn}
                                label="Filter Column"
                                onChange={(e) => {
                                    setSelectedColumn(String(e.target.value));
                                    setFilterValue("");
                                    setPage(0);
                                }}
                                displayEmpty
                            >

                                {FILTER_COLUMNS.map((col) => (
                                    <MenuItem key={col.value} value={col.value}>
                                        {col.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        {selectedColumn && (
                            <TextField
                                size="small"
                                label="Search"
                                value={filterValue}
                                onChange={(e) => {
                                    setFilterValue(e.target.value);
                                    setPage(0);
                                }}
                                placeholder="Type to search..."
                            />
                        )}
                    </Box>

                    <TableContainer sx={{ maxHeight: 520, border: "1px solid #ddd", borderRadius: 1 }}>
                        <Table stickyHeader size="small">
                            <TableHead sx={{ "& th": { backgroundColor: "#e0e0e0 !important", fontWeight: "bold" } }}>
                                <TableRow>
                                    <TableCell sortDirection={orderBy === "source_name" ? order : false} sx={{ minWidth: 150 }}>
                                        <TableSortLabel
                                            active={orderBy === "source_name"}
                                            direction={order}
                                            onClick={() => handleRequestSort("source_name" as SortKey)}
                                        >
                                            Source
                                        </TableSortLabel>
                                    </TableCell>

                                    <TableCell sortDirection={orderBy === "created_at" ? order : false} sx={{ minWidth: 170 }}>
                                        <TableSortLabel
                                            active={orderBy === "created_at"}
                                            direction={order}
                                            onClick={() => handleRequestSort("created_at" as SortKey)}
                                        >
                                            Date
                                        </TableSortLabel>
                                    </TableCell>

                                    <TableCell sortDirection={orderBy === "client_name" ? order : false} sx={{ minWidth: 205 }}>
                                        <TableSortLabel active={orderBy === "client_name"} direction={order} onClick={() => handleRequestSort("client_name")}>
                                            Name
                                        </TableSortLabel>
                                    </TableCell>

                                    <TableCell sortDirection={orderBy === "client_kyc_status" ? order : false}>
                                        <TableSortLabel
                                            active={orderBy === "client_kyc_status"}
                                            direction={order}
                                            onClick={() => handleRequestSort("client_kyc_status" as SortKey)}
                                        >
                                            KYC
                                        </TableSortLabel>
                                    </TableCell>

                                    <TableCell sortDirection={orderBy === "client_email" ? order : false}>
                                        <TableSortLabel active={orderBy === "client_email"} direction={order} onClick={() => handleRequestSort("client_email")}>
                                            Email
                                        </TableSortLabel>
                                    </TableCell>

                                    <TableCell sortDirection={orderBy === "client_mobile_number" ? order : false}>
                                        <TableSortLabel
                                            active={orderBy === "client_mobile_number"}
                                            direction={order}
                                            onClick={() => handleRequestSort("client_mobile_number" as SortKey)}
                                        >
                                            Mobile
                                        </TableSortLabel>
                                    </TableCell>

                                    <TableCell sortDirection={orderBy === "client_smest_id" ? order : false} sx={{ minWidth: 140 }}>
                                        <TableSortLabel
                                            active={orderBy === "client_smest_id"}
                                            direction={order}
                                            onClick={() => handleRequestSort("client_smest_id" as SortKey)}
                                        >
                                            SMEST ID
                                        </TableSortLabel>
                                    </TableCell>

                                    <TableCell sortDirection={orderBy === "client_lead_owner" ? order : false} sx={{ minWidth: 150 }}>
                                        <TableSortLabel
                                            active={orderBy === "client_lead_owner"}
                                            direction={order}
                                            onClick={() => handleRequestSort("client_lead_owner" as SortKey)}
                                        >
                                            Lead Owner
                                        </TableSortLabel>
                                    </TableCell>

                                    <TableCell sortDirection={orderBy === "active_referral_users_count" ? order : false}>
                                        <TableSortLabel
                                            active={orderBy === "active_referral_users_count"}
                                            direction={order}
                                            onClick={() => handleRequestSort("active_referral_users_count" as SortKey)}
                                        >
                                            Active
                                        </TableSortLabel>
                                    </TableCell>

                                    <TableCell sortDirection={orderBy === "invested_referral_count" ? order : false}>
                                        <TableSortLabel
                                            active={orderBy === "invested_referral_count"}
                                            direction={order}
                                            onClick={() => handleRequestSort("invested_referral_count" as SortKey)}
                                        >
                                            Invested
                                        </TableSortLabel>
                                    </TableCell>

                                    <TableCell sortDirection={orderBy === "reward_earned" ? order : false}>
                                        <TableSortLabel active={orderBy === "reward_earned"} direction={order} onClick={() => handleRequestSort("reward_earned" as SortKey)}>
                                            Earned
                                        </TableSortLabel>
                                    </TableCell>

                                    <TableCell sortDirection={orderBy === "reward_claimed" ? order : false}>
                                        <TableSortLabel
                                            active={orderBy === "reward_claimed"}
                                            direction={order}
                                            onClick={() => handleRequestSort("reward_claimed" as SortKey)}
                                        >
                                            Claimed
                                        </TableSortLabel>
                                    </TableCell>

                                    <TableCell>Action</TableCell>
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {paginated.map((client) => {
                                    const isExpanded = Boolean(expandedRows[client.client_uuid]);

                                    return (
                                        <React.Fragment key={client.client_uuid}>
                                            <TableRow hover>
                                                <TableCell>
                                                    {client.source?.name && client.source.name.trim() !== "" ? client.source.name : "SMEST"}
                                                </TableCell>
                                                <TableCell>{moment(client.created_at).format("YYYY-MM-DD HH:mm:ss")}</TableCell>
                                                <TableCell sx={{ fontWeight: 600 }}>{client.client_name}</TableCell>
                                                <TableCell>{String(client.client_kyc_status)}</TableCell>
                                                <TableCell>{client.client_email}</TableCell>
                                                <TableCell>{client.client_mobile_number}</TableCell>
                                                <TableCell>{client.client_smest_id}</TableCell>
                                                <TableCell>{client.client_lead_owner?.lead_owner ?? " "}</TableCell>
                                                <TableCell>{client.active_referral_users_count}</TableCell>
                                                <TableCell>{client.invested_referral_count}</TableCell>
                                                <TableCell>{client.reward_earned}</TableCell>
                                                <TableCell>{client.reward_claimed}</TableCell>
                                                <TableCell>
                                                    <IconButton size="small" onClick={() => toggleExpand(client.client_uuid)}>
                                                        {isExpanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>

                                            <TableRow>
                                                <TableCell colSpan={13} sx={{ p: 0, border: 0 }}>
                                                    <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                                        <Box sx={{ margin: 3 }}>
                                                            <Card sx={{ boxShadow: 2, borderRadius: 1 }}>
                                                                <CardContent sx={{ p: 1 }}>
                                                                    <Table size="small">
                                                                        <TableHead sx={{ "& th": { backgroundColor: "#f5f5f5 !important", fontWeight: "bold" } }}>
                                                                            <TableRow>
                                                                                <TableCell>Date</TableCell>
                                                                                <TableCell>Name</TableCell>
                                                                                <TableCell>KYC</TableCell>
                                                                                <TableCell>Email</TableCell>
                                                                                <TableCell>Mobile</TableCell>
                                                                                <TableCell>SMEST ID</TableCell>
                                                                                <TableCell>Lead Owner</TableCell>
                                                                                <TableCell>Lead Status</TableCell>
                                                                                <TableCell>Expires At</TableCell>
                                                                            </TableRow>
                                                                        </TableHead>
                                                                        <TableBody>
                                                                            {client.referrals?.map((r) => (
                                                                                <TableRow key={r.uuid} hover>
                                                                                    <TableCell>{moment(r.created_at).format("YYYY-MM-DD HH:mm:ss")}</TableCell>
                                                                                    <TableCell>{r.name}</TableCell>
                                                                                    <TableCell>{String(r.kyc_status)}</TableCell>
                                                                                    <TableCell>{r.email}</TableCell>
                                                                                    <TableCell>{r.mobile}</TableCell>
                                                                                    <TableCell>{r.smest_id}</TableCell>
                                                                                    <TableCell>{r.lead_owner}</TableCell>
                                                                                    <TableCell>{r.lead_status}</TableCell>
                                                                                    <TableCell>{moment(r.expires_at).format("YYYY-MM-DD HH:mm:ss")}</TableCell>
                                                                                </TableRow>
                                                                            ))}
                                                                        </TableBody>
                                                                    </Table>
                                                                </CardContent>
                                                            </Card>
                                                        </Box>
                                                    </Collapse>
                                                </TableCell>
                                            </TableRow>
                                        </React.Fragment>
                                    );
                                })}

                                {paginated.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={13} align="center" sx={{ py: 4 }}>
                                            <Typography>No records found.</Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <TablePagination
                        component="div"
                        count={filtered.length}
                        page={page}
                        onPageChange={handleChangePage}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        labelDisplayedRows={() => `${fromRecord}-${toRecord} of ${filtered.length}`}
                        rowsPerPageOptions={[5, 10, 25, 50]}
                    />
                </Box>
            )}
        </Paper>
    );
};

export default ReferralsUsers;
